import { Megaphone, Users2, CalendarClock, TrendingDown, Gauge, ClipboardCheck, Trophy, BellRing } from "lucide-react";
import { StakeholderPage } from "./StakeholderPage";

/**
 * /for/coaching-staff — the coach's purchase-decision page.
 * Coaches buy wins. Availability is the hidden stat behind every win column.
 */
export default function ForCoachingStaff() {
  return (
    <StakeholderPage
      testid="for-coach-page"
      eyebrow="For Coaching Staff"
      EyebrowIcon={Megaphone}
      headline="You don't lose games to the other team."
      headlineSub="You lose them to unavailability."
      intro={<>
        BioHacker Sports™ turns workload physics into <b className="text-white">lineup decisions you can defend</b>.
        Who is trending toward breakdown, who is ready for volume, who needs 48 hours — answered before practice,
        not after the MRI. Same load science the professional staffs run, shaped for your daily plan.
      </>}
      risksHeading="The four ways rosters quietly lose seasons."
      risksSub="And what a load-managed program does instead."
      risks={[
        { icon: Users2, label: "Best players unavailable in the games that matter", tone: "#F59E0B",
          body: "Late-season soft-tissue attrition is a workload story written in September. By the time an athlete pulls up lame, the overload pattern has been visible in the data for weeks.",
          relief: "Daily readiness + acute:chronic workload flags per athlete. You see the drift three weeks before the hamstring does." },
        { icon: CalendarClock, label: "Practice plans built on feel, not load", tone: "#22D3EE",
          body: "High-intensity days stacked back-to-back, travel fatigue ignored, throwing volume untracked — the plan feels right and quietly buries your two-deep.",
          relief: "Physics-based session load targets by sport and position. Plan tomorrow against what the roster actually absorbed today." },
        { icon: TrendingDown, label: "Development plateaus you can't explain", tone: "#A78BFA",
          body: "Velocity stalls, speed scores flatten, and nobody can say whether the athlete is under-trained, over-trained, or under-recovered.",
          relief: "Verified test history — 10-yd split, 30.6-Yard Split — Home-to-First, 60-yd, jumps, biomechanics — trended against workload so plateaus have a cause and a fix." },
        { icon: BellRing, label: "Finding out last", tone: "#F43F5E",
          body: "The trainer knew, the athlete knew, the group chat knew. The staff meeting is where you finally hear your Friday starter hasn't slept in four days.",
          relief: "One roster board with recovery, workload, and readiness flags — coach-visible the moment the data moves, not at the weekly sit-down." },
      ]}
      surfaceHeading="Your daily coaching surface."
      surfaceSub="Sixty seconds before practice, full clarity."
      surface={[
        { icon: Gauge, label: "Roster readiness board", body: "Green / amber / red by athlete with the reason attached — recovery score, workload spike, or flagged mechanics. Lineup calls with receipts." },
        { icon: ClipboardCheck, label: "Practice load dial", body: "Session-by-session load planning against each athlete's chronic base. Push the ready ones, protect the loaded ones, document both." },
        { icon: Trophy, label: "Verified development ledger", body: "Combine-grade test history and 2D/3D biomechanics on every athlete — proof the program develops players, for recruits and their parents." },
      ]}
      ctaHeading="Put load science behind every lineup call."
      ctaSub="Book a staff-scoped walkthrough. We'll run your sport's workload model against a sample roster."
      demoLabel="Request a coaching demo"
    />
  );
}
